import type { PlacedPumpkin, PumpkinKind, PumpkinLight, Scene } from '../types';
import { SCENE_VERSION } from '../types';
import { kindPreset } from '../pumpkin/shape';
import { PRESETS } from '../stencil/presets';
import { PORCH_DEFAULT } from './porch';
import { MAX_PUMPKINS, MAX_SCALE, MIN_SCALE } from './collide';

export const CANDLE: [number, number, number] = [1, 0.5, 0.16];
/** an LED tea light: steady and a whiter warm than a flame (it was violet once, which read as a fault) */
export const LED: [number, number, number] = [1, 0.74, 0.46];
const wasViolet = (c: unknown) => Array.isArray(c) && c[2] === 1 && c[1] === 0.3 && (c[0] === 0.55 || c[0] === 0.62);

export function defaultLight(): PumpkinLight {
  return { kind: 'candle', color: [...CANDLE], brightness: 1, flicker: 0.6, count: 3, rate: 1.5 };
}

const uid = () => 'p' + Math.random().toString(36).slice(2, 9);
const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x));

export function newPumpkin(kind: PumpkinKind, at: [number, number], face = 'classic', seed?: number): PlacedPumpkin {
  const preset = PRESETS.find(p => p.key === face) ?? PRESETS[0];
  const shape = kindPreset(kind, seed);
  const st = clone(preset.stencil);
  const stencil = { strokes: st.strokes, parts: st.parts, name: st.name, mirror: false, cut: true, carve: [] };
  return { id: uid(), shape, stencil, light: defaultLight(), at, turn: (Math.random() - 0.5) * 0.3, wall: 0.022 };
}

export function defaultScene(): Scene {
  const a = newPumpkin('field', [0.62, -2.0], 'classic', 1031);
  const b = newPumpkin('tall', [-1.62, -2.0], 'wicked', 77);
  const c = newPumpkin('mini', [0.05, -1.5], 'cute', 5);
  b.light = { ...b.light, kind: 'candles', count: 3 };
  c.light = { ...c.light, kind: 'led', color: [...LED], flicker: 0 };
  a.turn = 0.05; b.turn = 0.35; c.turn = -0.3;
  a.scale = 1.3; b.scale = 1.25;
  return { version: SCENE_VERSION, porch: clone(PORCH_DEFAULT), pumpkins: [a, b, c] };
}

/** a scene from anywhere (a link, local storage): missing fields filled, nothing trusted */
export function normaliseScene(raw: unknown): Scene | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<Scene>;
  if (!Array.isArray(s.pumpkins) || !s.porch) return null;
  // the night (fog, moon, sky) and the house's lean are fixed now: stored values are read and dropped
  const porch = { ...clone(PORCH_DEFAULT), ...s.porch, lamp: { ...PORCH_DEFAULT.lamp, ...(s.porch.lamp ?? {}) },
    mist: PORCH_DEFAULT.mist, moon: PORCH_DEFAULT.moon, jank: PORCH_DEFAULT.jank };
  const n = (v: unknown, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : undefined);
  const pumpkins = s.pumpkins.slice(0, MAX_PUMPKINS).filter(p => p && p.shape && p.stencil).map(p => ({
    ...p, light: { ...defaultLight(), ...p.light, ...(wasViolet(p.light?.color) ? { color: [...LED] } : {}) }, stencil: { strokes: p.stencil.strokes ?? [], parts: p.stencil.parts ?? [], name: p.stencil.name, mirror: !!p.stencil.mirror,
      cut: p.stencil.cut !== false, carve: Array.isArray(p.stencil.carve) ? p.stencil.carve : [] },
    scale: Number.isFinite(p.scale) ? Math.min(MAX_SCALE, Math.max(MIN_SCALE, p.scale!)) : undefined,
    at: Array.isArray(p.at) && p.at.length === 2 && p.at.every(Number.isFinite) ? p.at : [0, -0.5], turn: Number.isFinite(p.turn) ? p.turn : 0,
    wall: n(p.wall, 0.01, 0.06) ?? 0.022,
  })) as PlacedPumpkin[];
  const c = s.camera, t = c?.target;
  const camera = c && Array.isArray(t) && t.length === 3 && t.every(Number.isFinite) && [c.yaw, c.pitch, c.dist].every(Number.isFinite)
    ? { yaw: c.yaw, pitch: c.pitch, dist: n(c.dist, 0.3, 12)!, target: [t[0], t[1], t[2]] as [number, number, number], roll: n(c.roll, -0.35, 0.35), fov: n(c.fov, 0.4, 1.3) }
    : undefined;
  const filter = n(s.look?.filter, 0, 4);
  return { version: SCENE_VERSION, title: s.title, porch, pumpkins, camera, look: filter ? { filter: Math.round(filter) } : undefined };
}
